import React, { useEffect, useMemo, useState } from 'react';
import { useRoutesStore } from '../../../../store/routesStore';
import { DeliveryRoute, listRouteStops } from '../../../../services/routesApi';
import { listOrders, Order } from '../../../../services/ordersApi';
import { formatOrderId } from '../../../../utils/formatId';
import styles from './EditRouteModal.module.css';

interface AddOrderToRouteModalProps {
  open: boolean;
  route: DeliveryRoute | null;
  onClose: () => void;
}

/**
 * Attach a shipped (picked) order to a delivery route as a stop.
 * Picks from orders with status 'shipped' — those that have completed pick-pack
 * but haven't been delivered yet.
 *
 * Sequence defaults to (existing stop count + 1) on the chosen route, so the
 * new stop lands at the end. The user can override before submitting.
 */
export const AddOrderToRouteModal: React.FC<AddOrderToRouteModalProps> = ({ open, route, onClose }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderId, setOrderId] = useState<string>('');
  const [sequence, setSequence] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addStop = useRoutesStore((s) => s.addStop);

  useEffect(() => {
    if (!open || !route) return;
    setOrderId(''); setNotes(''); setError(null); setSubmitting(false);
    setLoadingOrders(true);
    Promise.all([
      listOrders({ status: 'shipped', limit: 100 }),
      listRouteStops(route.id),
    ])
      .then(([ordersResp, stops]) => {
        setOrders(ordersResp.data ?? []);
        setSequence(stops.length + 1);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load orders'))
      .finally(() => setLoadingOrders(false));
  }, [open, route]);

  const orderOptions = useMemo(
    () => orders.map((o) => ({
      id: o.id,
      label: `${formatOrderId(o.id)} — ${o.customer_name || `Customer #${o.customer_id}`} — $${Number(o.total_amount).toFixed(2)}`,
    })),
    [orders]
  );

  if (!open || !route) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId) { setError('Pick an order'); return; }
    if (!Number.isInteger(sequence) || sequence < 1) { setError('Sequence must be a positive integer'); return; }
    setError(null); setSubmitting(true);
    try {
      await addStop(route.id, {
        order_id: Number(orderId),
        sequence,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add order to route');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Add Order to {route.route_name}</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}
          <p className={styles.note}>
            Only orders with status <strong>Shipped</strong> appear here — these are orders that have completed pick-pack and are ready to be delivered.
          </p>
          <label className={styles.field}>
            <span>Order <span className={styles.required}>*</span></span>
            <select
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              disabled={loadingOrders}
              required
            >
              <option value="">
                {loadingOrders
                  ? 'Loading…'
                  : orderOptions.length === 0
                    ? 'No shipped orders available'
                    : '— Select a shipped order —'}
              </option>
              {orderOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>Sequence (stop order on this route)</span>
            <input
              type="number"
              min={1}
              value={sequence}
              onChange={(e) => setSequence(parseInt(e.target.value, 10) || 1)}
            />
          </label>
          <label className={styles.field}>
            <span>Notes (optional)</span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Gate code, dock instructions, etc."
              maxLength={200}
            />
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting || loadingOrders}>
              {submitting ? 'Adding…' : 'Add Stop'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrderToRouteModal;
